import { Check, ListOrdered, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Pattern } from '../types';

/**
 * Type the slice order out by hand.
 *
 * Dragging cells is fine for nudging one step, but useless for "I want
 * 1 1 5 1 3 3 8 2". This is the same pattern as a text field, and it round-trips:
 * whatever the grid shows, this shows, and vice versa.
 */
export function OrderField({
  pattern,
  onApply,
}: {
  pattern: Pattern;
  onApply: (slices: number[]) => void;
}) {
  const format = (p: Pattern) => p.steps.map((s) => (s.slice < 0 ? '-' : s.slice + 1)).join(' ');
  const [text, setText] = useState(() => format(pattern));
  const [dirty, setDirty] = useState(false);
  const focused = useRef(false);

  // Follow the grid unless the user is mid-edit.
  useEffect(() => {
    if (!focused.current) {
      setText(format(pattern));
      setDirty(false);
    }
  }, [pattern]);

  const parse = (raw: string): number[] =>
    raw
      .split(/[\s,|]+/)
      .filter(Boolean)
      .map((tok) => {
        if (/^[-._x]$/i.test(tok)) return -1;
        const n = parseInt(tok, 10);
        return Number.isFinite(n) && n > 0 ? n - 1 : -1;
      });

  const apply = () => {
    // Blur fires when you click a transform button too. Re-applying unchanged
    // text there would push a pointless undo entry.
    if (!dirty) return;
    const slices = parse(text);
    if (slices.length) onApply(slices);
    setDirty(false);
  };

  return (
    <div className="flex items-center gap-1.5">
      <ListOrdered size={13} className="shrink-0 text-ink-400" />
      <span className="label shrink-0">Order</span>
      <input
        className="field mono min-w-0 flex-1"
        value={text}
        spellCheck={false}
        placeholder="1 2 3 4  ·  use - for a rest"
        onFocus={() => (focused.current = true)}
        onBlur={() => {
          focused.current = false;
          apply();
        }}
        onChange={(e) => {
          setText(e.target.value);
          setDirty(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setText(format(pattern));
            setDirty(false);
            e.currentTarget.blur();
          }
          e.stopPropagation();
        }}
      />
      <button
        className={`btn btn-icon ${dirty ? 'btn-primary' : ''}`}
        title="Apply this order"
        disabled={!dirty}
        onClick={apply}
      >
        <Check size={12} />
      </button>
      <button
        className="btn btn-icon"
        title="Revert to what the grid shows"
        disabled={!dirty}
        onClick={() => {
          setText(format(pattern));
          setDirty(false);
        }}
      >
        <RotateCcw size={12} />
      </button>
    </div>
  );
}
